import React, { useRef, useState } from "react";
import { Plus, Edit, Trash2, Printer, Eye, Download, Upload, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { STATUS_TONE, WORK_FILE_KINDS, workFileUrl, fmtDate, rupee, isAdmin } from "../../lib/customers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { WorkForm } from "./WorkForm";
import { WorkPrint } from "./PrintViews";

const Info = ({ l, v }) => <div><div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div><div className="text-slate-900 break-words">{v || "—"}</div></div>;

const WorkFiles = ({ cid, w, reload }) => {
  const [kind, setKind] = useState(WORK_FILE_KINDS[0]);
  const ref = useRef(null);
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", f);
    try { await api.post(`/customers/${cid}/works/${w.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${kind} uploaded`); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const del = async (f) => {
    if (!window.confirm(`${f.kind} delete karein?`)) return;
    try { await api.delete(`/customers/${cid}/works/${w.id}/files/${f.id}`); reload(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="mt-3 rounded-md border border-slate-100 bg-slate-50/60 p-3">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Files / Receipts</div>
      {(w.files || []).map((f) => (
        <div key={f.id} className="flex items-center justify-between gap-2 text-sm py-1" data-testid={`work-file-${f.id}`}>
          <span className="truncate"><b>{f.kind}</b> <span className="text-xs text-slate-500">· {fmtDate(f.uploaded_at, true)} · {f.uploaded_by}</span></span>
          <span className="flex gap-1 shrink-0">
            <a href={workFileUrl(cid, w.id, f.id)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-white" title="View" data-testid={`work-file-view-${f.id}`}><Eye className="w-4 h-4" /></a>
            <a href={workFileUrl(cid, w.id, f.id, true)} className="p-1.5 rounded hover:bg-white" title="Download" data-testid={`work-file-download-${f.id}`}><Download className="w-4 h-4" /></a>
            {isAdmin() && <button onClick={() => del(f)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete" data-testid={`work-file-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>}
          </span>
        </div>
      ))}
      <div className="flex flex-wrap gap-2 mt-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid={`work-file-kind-${w.id}`}>
          {WORK_FILE_KINDS.map((k) => <option key={k}>{k}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid={`work-file-upload-${w.id}`}><Upload className="w-3 h-3 mr-1" /> Upload</Button>
        <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.pdf,.webp" className="hidden" onChange={up} data-testid={`work-file-input-${w.id}`} />
      </div>
    </div>
  );
};

const Vault = ({ cid, w, reload }) => {
  const [pw, setPw] = useState("");
  const [shown, setShown] = useState(null);
  const save = async () => {
    try { await api.put(`/customers/${cid}/works/${w.id}/vault`, { password: pw }); toast.success(pw ? "Encrypted save ho gaya" : "Vault clear"); setPw(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const show = async () => {
    try { const r = await api.get(`/customers/${cid}/works/${w.id}/vault`); setShown(r.data.password); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-3 rounded-md border border-purple-100 bg-purple-50/40 p-3 text-sm" data-testid={`vault-${w.id}`}>
      <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold text-purple-800 mb-2"><Lock className="w-3 h-3" /> Credential Vault (encrypted · sirf Admin dekh sakta hai)</div>
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs">{w.has_vault ? "Password saved ✓" : "Koi password saved nahi"}</span>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Portal password" className="h-8 w-48" data-testid={`vault-input-${w.id}`} />
        <Button size="sm" variant="outline" onClick={save} data-testid={`vault-save-${w.id}`}>Save</Button>
        {isAdmin() && w.has_vault && <Button size="sm" variant="outline" onClick={show} data-testid={`vault-show-${w.id}`}><KeyRound className="w-3 h-3 mr-1" /> Show</Button>}
        {shown && <code className="px-2 py-1 bg-white rounded border text-xs" data-testid={`vault-value-${w.id}`}>{shown}</code>}
      </div>
    </div>
  );
};

const WorkCard = ({ c, w, onEdit, onPrint, reload }) => {
  const del = async () => {
    if (!window.confirm(`${w.work_code} delete karein?`)) return;
    try { await api.delete(`/customers/${c.id}/works/${w.id}`); toast.success("Deleted"); reload(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`work-card-${w.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-xs text-slate-500">{w.work_code} · {fmtDate(w.created_at)} · {w.branch_name}</div>
          <div className="text-lg font-semibold text-slate-900">{w.service_name} {w.category && <span className="text-sm font-normal text-slate-500">· {w.category}</span>}</div>
        </div>
        <div className="flex items-center gap-1">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_TONE[w.status] || ""}`} data-testid={`work-status-${w.id}`}>{w.status}</span>
          <Button size="icon" variant="ghost" onClick={onPrint} title="Print Work Details" data-testid={`work-print-${w.id}`}><Printer className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={onEdit} title="Edit" data-testid={`work-edit-${w.id}`}><Edit className="w-4 h-4" /></Button>
          {isAdmin() && <Button size="icon" variant="ghost" className="text-red-600" onClick={del} title="Delete" data-testid={`work-delete-${w.id}`}><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </div>
      {w.description && <div className="text-sm text-slate-700 mt-1">{w.description}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
        <Info l="Application No" v={w.application_no} /><Info l="Registration No" v={w.registration_no} />
        <Info l="Application Date" v={w.application_date && fmtDate(w.application_date)} /><Info l="Last Date" v={w.last_date && fmtDate(w.last_date)} />
        <Info l="Portal Login ID" v={w.portal_login_id} /><Info l="Reg. Email" v={w.registered_email} /><Info l="Reg. Mobile" v={w.registered_mobile} />
        <Info l="Assigned" v={w.assigned_operator} />
        <Info l="Total" v={rupee(w.total_amount)} /><Info l="Paid" v={rupee(w.paid_amount)} /><Info l="Due" v={rupee(w.due_amount)} /><Info l="Payment" v={w.payment_status} />
        <Info l="Next Follow-up" v={w.next_followup_at && fmtDate(w.next_followup_at, true)} /><Info l="Completed" v={w.completed_at && fmtDate(w.completed_at, true)} />
      </div>
      {w.notes && <div className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">Notes: {w.notes}</div>}
      <WorkFiles cid={c.id} w={w} reload={reload} />
      <Vault cid={c.id} w={w} reload={reload} />
    </div>
  );
};

export const WorksTab = ({ c, works, reload }) => {
  const [editing, setEditing] = useState(null);
  const [printing, setPrinting] = useState(null);
  return (
    <div className="mt-3 space-y-3" data-testid="works-tab">
      <div className="flex justify-end"><Button onClick={() => setEditing({})} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="add-work-btn"><Plus className="w-4 h-4 mr-1" /> Add New Work</Button></div>
      {works.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500">Abhi koi kaam record nahi hai.</div>}
      {works.map((w) => <WorkCard key={w.id} c={c} w={w} reload={reload} onEdit={() => setEditing(w)} onPrint={() => setPrinting(w)} />)}
      {editing && <WorkForm cid={c.id} work={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {printing && <WorkPrint c={c} w={printing} onClose={() => setPrinting(null)} />}
    </div>
  );
};
